import { useTileHover } from '@components/shared/tile-context';
import { cn } from '@lib/utils';
import React, { type ReactNode, cloneElement, isValidElement } from 'react';

type IconButtonVariant = 'active' | 'danger' | 'destructive' | 'outline' | 'primary' | 'secondary' | 'text';
type IconComponent = React.ComponentType<{ size?: number }> | React.ExoticComponent<{ size?: number }>;

type IconButtonProps = React.ComponentProps<'button'> & {
  ariaLabel?: string;
  className?: string;
  icon: IconComponent | ReactNode;
  isVisible?: boolean;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  variant?: IconButtonVariant;
};

const isIconComponent = (icon: IconComponent | ReactNode): icon is IconComponent =>
  typeof icon === 'function' || (typeof icon === 'object' && icon !== null && '$$typeof' in icon);

const sizeConfig = {
  lg: {
    buttonSize: 'w-10 h-10 min-w-10 min-h-10',
    iconSize: 22,
  },
  md: {
    buttonSize: 'w-8 h-8 min-w-8 min-h-8',
    iconSize: 16,
  },
  sm: {
    buttonSize: 'w-6 h-6 min-w-6 min-h-6',
    iconSize: 14,
  },
};

const variantStyles = {
  active: 'bg-[#3D2EFF1A] text-[#3D2EFF] border-transparent',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  destructive: 'border border-transparent bg-red-100 text-red-600 hover:bg-red-200',
  outline: 'border border-gray-200 bg-transparent text-[#1F1C1A] hover:bg-[#F5F5F5]',
  primary: 'bg-[#3D2EFF] text-white hover:bg-[#3D2EFF]/90',
  secondary: 'bg-[#F5F5F5] text-[#1F1C1A] hover:bg-[#3D2EFF]/10',
  text: 'bg-transparent text-[#1F1C1A] hover:bg-[#F5F5F5]',
};

const renderIcon = (icon: IconComponent | ReactNode, iconSize: number): ReactNode => {
  if (isValidElement<{ size?: number }>(icon)) {
    return cloneElement(icon, {
      size: iconSize,
      ...icon.props,
    });
  }

  if (isIconComponent(icon)) {
    return React.createElement(icon, { size: iconSize });
  }

  return icon;
};

const IconButtonBase = (props: IconButtonProps) => {
  const {
    ariaLabel,
    className,
    disabled = false,
    icon,
    isVisible = true,
    loading = false,
    size = 'md',
    title,
    type,
    variant = 'primary',
    ...otherProps
  } = props;

  const stateStyles = disabled
    ? 'opacity-50 cursor-not-allowed'
    : loading
      ? 'opacity-70 cursor-wait'
      : 'cursor-pointer';

  const iconSize = sizeConfig[size].iconSize;

  return (
    <button
      aria-label={ariaLabel}
      className={cn(
        'flex items-center justify-center rounded-md transition-colors',
        sizeConfig[size].buttonSize,
        variantStyles[variant],
        stateStyles,
        !isVisible && 'opacity-0',
        className,
      )}
      disabled={disabled || loading}
      title={title || ariaLabel}
      type={type ?? 'button'}
      {...otherProps}
    >
      {loading ? <div className="animate-spin">{renderIcon(icon, iconSize)}</div> : renderIcon(icon, iconSize)}
    </button>
  );
};

IconButtonBase.displayName = 'IconButton';

type HoverRevealProps = Omit<IconButtonProps, 'isVisible'> & {
  /**
   * Force the button to be visible regardless of hover state.
   * Useful when a dropdown menu is open and mouse leaves the tile.
   */
  forceVisible?: boolean;
};

/**
 * IconButton that auto-reveals on Tile hover.
 * Uses `useTileHover()` context - only works inside a Tile with `hoverReveal` enabled.
 * Falls back to always visible when used outside Tile context.
 */
const HoverReveal = (props: HoverRevealProps) => {
  const { forceVisible = false, ...otherProps } = props;
  const isHovering = useTileHover();

  return (
    <IconButtonBase
      isVisible={isHovering || forceVisible}
      {...otherProps}
    />
  );
};

HoverReveal.displayName = 'IconButton.HoverReveal';

export const IconButton = Object.assign(IconButtonBase, {
  HoverReveal,
});

export type { IconButtonProps };
